import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext } from "react";
import classnames from "classnames";
import PricingCheck from "/static/svg/pricing-check.svg";
import TooltipComponent from "components/enhancements/tooltip/tooltip-component";
import { DARK_THEME } from "components/enhancements/tooltip/tooltip-constants";
import PricingInfoIcon from "/static/svg/pricing-info-icon.svg";
import productPricingConfig from "segments/desktop/pricing/product-pricing-config";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { TIERS } from "constants/pricing";
import { translateProductKeyFallbackToCore } from "/services/pricing-features-service";
import { trackEvent } from "services/bigbrain-service";
import { PRICING_PAGE_FEATURE_INFO_ICON_HOVER } from "constants/bigbrain-event-types";
import MondayMarkupLanguageComponent from "client/components/system/monday-markup-language/monday-markup-language-component";
import { StyledProductFeatureValue, StyledProductFeatureName } from "./product-features-table-feature.scss";
import NewPricingCheck from "/static/svg/new-pricing-check.svg";
const isLimitNumeric = (limit)=>{
    return !isNaN(limit);
};
export const ProductFeaturesTableFeature = ({ productName, feature, hideFreeTier, hasFreeTier, isNewPricing, shouldHideBasicTier, isCrmClassic })=>{
    const { featureName, tierColumnValues } = feature;
    const { translate, localeId } = useContext(GenericConfigContext);
    const product = productPricingConfig[productName];
    const productFeatureName = translateProductKeyFallbackToCore({
        productName,
        key: featureName
    });
    const productFeatureDescription = translateProductKeyFallbackToCore({
        productName,
        key: `${featureName}.description`
    });
    // translateProductKeyFallbackToCore returns the raw key (prefixed with
    // "pricingPage.products") when no copy exists in either the product or core
    // namespace — in CRM classic we only render a tooltip when real copy exists.
    const hasDescription = !productFeatureDescription.startsWith("pricingPage.products");
    const getProductTiers = useCallback(()=>{
        let tiers = [
            ...product.tiers
        ];
        if (!hasFreeTier || hideFreeTier) {
            tiers = tiers.filter((tier)=>tier.name !== TIERS.FREE);
        }
        if (shouldHideBasicTier) {
            tiers = tiers.filter((tier)=>tier.name !== TIERS.BASIC);
        }
        return tiers;
    }, [
        hasFreeTier,
        hideFreeTier,
        product,
        shouldHideBasicTier
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(StyledProductFeatureName, {
                className: classnames("feature-name", {
                    "tooltip-on-text": isCrmClassic
                }),
                isNewPricing: isNewPricing,
                isCrmClassic: isCrmClassic,
                children: isCrmClassic ? // CRM classic: no info icon — the tooltip shows on hover over the
                // feature name itself (only when real description copy exists).
                /*#__PURE__*/ _jsxs("div", {
                    className: "name-label",
                    onMouseEnter: hasDescription ? ()=>trackEvent(PRICING_PAGE_FEATURE_INFO_ICON_HOVER, {
                            kind: featureName,
                            productKind: productName,
                            placement: "feature_list"
                        }) : undefined,
                    children: [
                        hasDescription && /*#__PURE__*/ _jsx("div", {
                            className: "tooltip",
                            children: /*#__PURE__*/ _jsx(TooltipComponent, {
                                theme: DARK_THEME,
                                className: "floating-tooltip",
                                children: productFeatureDescription
                            })
                        }),
                        /*#__PURE__*/ _jsx(MondayMarkupLanguageComponent, {
                            tagName: "span",
                            text: productFeatureName
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(MondayMarkupLanguageComponent, {
                            tagName: "span",
                            text: productFeatureName
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "icon",
                            onMouseEnter: ()=>trackEvent(PRICING_PAGE_FEATURE_INFO_ICON_HOVER, {
                                    kind: featureName,
                                    productKind: productName,
                                    placement: "feature_list"
                                }),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "tooltip",
                                    children: /*#__PURE__*/ _jsxs(TooltipComponent, {
                                        theme: DARK_THEME,
                                        className: "floating-tooltip",
                                        children: [
                                            " ",
                                            productFeatureDescription
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(PricingInfoIcon, {})
                            ]
                        })
                    ]
                })
            }),
            getProductTiers().map((tier)=>{
                const value = tierColumnValues[tier.name];
                if (value === true) {
                    return /*#__PURE__*/ _jsx(StyledProductFeatureValue, {
                        className: classnames("feature-value", "check-circle"),
                        isNewPricing: isNewPricing,
                        children: isNewPricing ? /*#__PURE__*/ _jsx(NewPricingCheck, {}) : /*#__PURE__*/ _jsx(PricingCheck, {})
                    });
                }
                if (value === undefined || value === null) {
                    return /*#__PURE__*/ _jsx(StyledProductFeatureValue, {
                        className: classnames("feature-value", "check-circle"),
                        isNewPricing: isNewPricing,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "empty-feature-value"
                        })
                    });
                }
                if (typeof value === "number") {
                    return /*#__PURE__*/ _jsx(StyledProductFeatureValue, {
                        className: "feature-value",
                        isNewPricing: isNewPricing,
                        children: value.toLocaleString(localeId)
                    });
                }
                if (typeof value === "object" && value.limit) {
                    return /*#__PURE__*/ _jsxs(StyledProductFeatureValue, {
                        className: "feature-value",
                        isNewPricing: isNewPricing,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "bold",
                                children: isLimitNumeric(value.limit) ? parseInt(value.limit).toLocaleString(localeId) : translate(value.limit)
                            }),
                            " ",
                            translate(value.description)
                        ]
                    });
                }
                if (typeof value === "object" && value.key) {
                    var _translate;
                    return /*#__PURE__*/ _jsx(StyledProductFeatureValue, {
                        className: "feature-value",
                        isNewPricing: isNewPricing,
                        children: (_translate = translate(value.key)) === null || _translate === void 0 ? void 0 : _translate.replace("{amount}", value.amount)
                    });
                }
                return /*#__PURE__*/ _jsx(StyledProductFeatureValue, {
                    className: "feature-value",
                    isNewPricing: isNewPricing,
                    children: translate(value)
                });
            })
        ]
    });
};
